/**
 * @jsxRuntime classic
 * @jsx jsx
 */

import { useCallback, useId, useRef, useState } from 'react';

import { jsx, cssMap as unboundedCssMap } from '@compiled/react';

import { IconButton } from '@atlaskit/button/new';
import { cssMap, cx } from '@atlaskit/css';
import ChevronDownIcon from '@atlaskit/icon/core/chevron-down';
import ChevronUpIcon from '@atlaskit/icon/core/chevron-up';
import { Box, Inline } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import { useLuminaAnalyticsEvents } from '../../analytics/use-lumina-analytics-events';
import { AnimatedHeader } from '../animated/animated-header';
import type { HeaderElementType, ParsedElementType } from '../types';

import type { RenderElementOptions } from './types';

interface FoldableWrapperProps {
	children: React.ReactNode;
	content: ParsedElementType[];
	defaultFoldedState?: 'collapsed' | 'expanded';
	header: HeaderElementType | undefined;
	isStreaming?: boolean;
	renderElement: (options: RenderElementOptions) => React.JSX.Element | null;
	sectionKey?: string;
}

const MAX_EXPANDED_HEIGHT = '500px';

// Only webkit-specific styles that require @compiled/react
const unboundedStyles = unboundedCssMap({
	lineClamp: {
		display: '-webkit-box',
		WebkitBoxOrient: 'vertical',
		WebkitLineClamp: 1,
		overflow: 'hidden',
	},
});

const styles = cssMap({
	root: {
		marginTop: token('space.200'),
		width: '100%',
	},
	rootPointer: {
		cursor: 'pointer',
	},
	contentWrapper: {
		overflow: 'hidden',
		display: 'flex',
		flexDirection: 'column',
	},
	contentExpanded: {
		maxHeight: MAX_EXPANDED_HEIGHT,
	},
	contentCollapsed: {
		maxHeight: '64px',
	},
	divider: {
		borderTopStyle: 'solid',
		borderTopWidth: token('border.width'),
		borderTopColor: token('color.border'),
		marginTop: token('space.200'),
	},
	headerWrapper: {
		marginBottom: token('space.100'),
	},
});

/**
 * FoldableWrapper - A reusable wrapper for making any layout foldable
 * This is used by foldable layout renderers, not directly by LuminaSection
 *
 * Accessibility notes:
 * - When collapsed: Clickable section is interactive (expands on click/keyboard)
 * - When expanded: Only IconButton is interactive (collapses on click)
 * - IconButton has aria-expanded and aria-controls
 * - Content region has proper ARIA semantics with aria-labelledby
 * - Preview content remains accessible when collapsed (no aria-hidden)
 */
export const FoldableWrapper = ({
	defaultFoldedState = 'expanded',
	header,
	content,
	children,
	isStreaming = false,
	renderElement,
	sectionKey,
}: FoldableWrapperProps): React.JSX.Element => {
	const { fireEvent } = useLuminaAnalyticsEvents();
	const containerRef = useRef(null);
	const [isFolded, setIsFolded] = useState<boolean>(defaultFoldedState === 'collapsed');

	// Generate unique IDs for ARIA relationships
	const contentId = useId();
	const headerId = useId();
	const headerText = (header?.children as string) || 'section';

	// Find the best element for preview when collapsed
	// Prefer text-based elements (md, list) over visual elements (carousel, smart-link, etc.)
	const previewElement =
		content.length === 0
			? undefined
			: content.find((element) => {
					if (typeof element === 'string') {
						return true;
					}
					return (
						element.tag === 'md' ||
						element.tag === 'ul' ||
						element.tag === 'ol' ||
						element.tag === 'list'
					);
				}) || content[0];

	const handleToggle = useCallback(
		(newFolded: boolean) => {
			setIsFolded(newFolded);
			fireEvent('ui.lumina.interacted', {
				componentType: 'section',
				interactionType: 'toggled',
				sectionKey: sectionKey ?? null,
				newState: newFolded ? 'collapsed' : 'expanded',
			});
		},
		[fireEvent, sectionKey],
	);

	// Only handle keyboard events when collapsed
	const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
		if ((event.key === 'Enter' || event.key === ' ') && isFolded) {
			event.preventDefault();
			handleToggle(false);
		}
	};

	return (
		<Box
			xcss={cx(styles.root, isFolded && styles.rootPointer)}
			testId="lumina-layout-foldable-section"
		>
			<Inline>
				<IconButton
					icon={isFolded ? ChevronDownIcon : ChevronUpIcon}
					label={`${isFolded ? 'Expand' : 'Collapse'} ${headerText}`}
					appearance="subtle"
					spacing="compact"
					onClick={() => handleToggle(!isFolded)}
					testId={isFolded ? 'expand-button' : 'collapse-button'}
					aria-expanded={!isFolded}
					aria-controls={contentId}
				/>
				<div
					ref={containerRef}
					css={[styles.contentWrapper, isFolded ? styles.contentCollapsed : styles.contentExpanded]}
					onClick={isFolded ? () => handleToggle(false) : undefined}
					onKeyDown={isFolded ? handleKeyDown : undefined}
					tabIndex={isFolded ? 0 : undefined}
					role={isFolded ? 'button' : undefined}
					aria-label={isFolded ? `Expand ${headerText}` : undefined}
					aria-controls={isFolded ? contentId : undefined}
					data-testid="lumina-layout-foldable-clickable-section"
				>
					<Inline alignBlock="center" xcss={styles.headerWrapper}>
						{header && header.attributes?.show !== 'false' && content && (
							<AnimatedHeader id={headerId} isStreaming={isStreaming}>
								{header.children as string}
							</AnimatedHeader>
						)}
					</Inline>
					<Box id={contentId} role="region" aria-labelledby={headerId}>
						{isFolded ? (
							<div css={unboundedStyles.lineClamp} key={`${sectionKey}-collapsed`}>
								{previewElement &&
									renderElement({
										component: previewElement,
										position: 'single-column',
										isFoldable: true,
									})}
							</div>
						) : (
							<Box key={`${sectionKey}-expanded`}>{children}</Box>
						)}
					</Box>
				</div>
			</Inline>
			<Box xcss={styles.divider} />
		</Box>
	);
};
